/**
 * ----------------------------------------------------------------------------
 * COPY CODE
 * ----------------------------------------------------------------------------
 * Custom addition to the Impact theme. Not part of Maestrooo's base code, so it
 * lives in its own asset and is loaded only by the snippet that needs it.
 *
 * ********************************************
 * The code is readable without this file
 * ********************************************
 *
 * The discount code is printed in the markup by Liquid. This script only adds
 * one-tap copying on top of it. If it never loads, or the clipboard is refused
 * (a page served over http, an older browser, a hardened profile), the shopper
 * still reads the code and types it, so nothing is lost but the shortcut.
 */

const FEEDBACK_DURATION = 2000;

/**
 * Wraps a button that copies the discount code it carries.
 *
 * Attributes:
 *   code - the discount code to write to the clipboard
 */
class CopyCode extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.label = this.querySelector('[data-copy-label]');

    if (!this.button || !navigator.clipboard) {
      /* Nothing to enhance: leave the code as plain readable text. */
      return;
    }

    this.defaultLabel = this.label?.textContent.trim();
    this._onClickListener = this.onClick.bind(this);
    this.button.addEventListener('click', this._onClickListener);
  }

  disconnectedCallback() {
    clearTimeout(this.timeout);
    this.button?.removeEventListener('click', this._onClickListener);
  }

  /**
   * Copy the code, then confirm it in place for a couple of seconds.
   *
   * @returns {Promise<void>}
   */
  async onClick() {
    try {
      await navigator.clipboard.writeText(this.getAttribute('code'));
    } catch {
      /* Permission denied or an insecure context: the printed code still works. */
      return;
    }

    if (!this.label) {
      return;
    }

    /* aria-live on the label announces the swap, so the confirmation is not
       purely visual for someone using a screen reader. */
    this.label.textContent = this.label.dataset.copiedLabel || this.label.textContent;
    this.setAttribute('copied', '');

    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.removeAttribute('copied');
      this.label.textContent = this.defaultLabel;
    }, FEEDBACK_DURATION);
  }
}

if (!customElements.get('copy-code')) {
  customElements.define('copy-code', CopyCode);
}
